import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Clock, DollarSign, Mail, Phone, Download, User, Music, CheckCircle, AlertCircle } from "lucide-react";
import { downloadICalFile } from "../utils/icalGenerator";
import { formatDate, formatDateWithDay } from "../utils/dateFormat";
import { ORG_PHONE_RAW, ORG_EMAIL_BOOKINGS } from "../constants";
import Footer from "../components/Footer";
import LanguageSelector from "../components/LanguageSelector";

const DashboardPage = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [sessions, setSessions] = useState({
    upcoming_sessions: [],
    past_sessions: [],
    stats: {
      upcoming_count: 0,
      completed_this_month: 0,
      allowance_earned_this_month: 0,
      is_external_kj: false
    },
    kj_name: "",
    kj_type: ""
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Redirect admins away from KJ pages
    if (user?.is_admin) {
      navigate('/admin');
      return;
    }
    fetchSessions();
  }, [user, navigate]);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/kj/my-sessions`);
      setSessions(response.data);
    } catch (error) {
      console.error("Failed to fetch sessions", error);
      toast.error("Failed to fetch your schedule");
    } finally {
      setLoading(false);
    }
  };

  const handleNeedHelp = () => {
    const subject = encodeURIComponent(`Schedule Support Request - ${user?.name}`);
    const body = encodeURIComponent(
      `Dear YCK Court RN Karaoke Admin,\n\n` +
      `Name: ${user?.name}\n` +
      `Email: ${user?.email}\n\n` +
      `I need assistance with:\n` +
      `[Please describe your issue here]\n\n` +
      `Best regards`
    );
    window.location.href = `mailto:${ORG_EMAIL_BOOKINGS}?subject=${subject}&body=${body}`;
  };

  const getStatusBadge = (status) => {
    if (status === 'completed') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-200 text-gray-700 text-xs font-bold border-2 border-black">
          <CheckCircle size={14} />
          COMPLETED
        </span>
      );
    }
    if (status === 'paid') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 bg-success text-white text-xs font-bold border-2 border-black">
          <CheckCircle size={14} />
          CONFIRMED
        </span>
      );
    }
    if (status === 'pending') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 text-xs font-bold border-2 border-black">
          <AlertCircle size={14} />
          PENDING PAYMENT
        </span>
      );
    }
    return (
      <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-bold border-2 border-black">
        {status?.toUpperCase()}
      </span>
    );
  };

  const getSlotTime = (slotType) => {
    return slotType === 'afternoon' ? '1:00 PM - 5:00 PM' : '6:00 PM - 10:00 PM';
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b-4 border-black bg-secondary py-4 sm:py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center mb-3">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-sm font-bold hover:underline"
            >
              <ArrowLeft size={16} />
              BACK HOME
            </button>
            <LanguageSelector />
          </div>
          
          {/* Title + User Info */}
          <div className="mb-3">
            <h1 className="text-2xl sm:text-4xl font-syne font-bold mb-1 flex items-center gap-2 sm:gap-3">
              <Music className="text-purple-600 shrink-0" size={24} />
              MY SCHEDULE
            </h1>
            <p className="text-muted-foreground flex items-center gap-2 text-sm">
              <User size={14} />
              <span className="notranslate font-bold" translate="no">{user?.name}</span>
              <span className="ml-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded font-bold">
                {sessions.kj_type || "Member"}
              </span>
            </p>
          </div>

          {/* Action Buttons - horizontal scroll on mobile */}
          <div className="flex gap-2 sm:gap-4 overflow-x-auto pb-1">
            <button
              onClick={() => navigate('/kj-console')}
              className="brutal-btn-primary flex items-center gap-2 text-sm sm:text-base whitespace-nowrap shrink-0"
              data-testid="kj-console-btn"
            >
              <Music size={16} />
              KJ CONSOLE
            </button>
            <button
              onClick={() => navigate('/profile')}
              className="brutal-btn-secondary flex items-center gap-2 text-sm sm:text-base whitespace-nowrap shrink-0"
              data-testid="my-account-btn"
            >
              <User size={16} />
              MY ACCOUNT
            </button>
            <button
              onClick={logout}
              className="brutal-btn-outline text-sm sm:text-base whitespace-nowrap shrink-0"
              data-testid="logout-btn"
            >
              LOGOUT
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-12">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          {/* Upcoming Sessions */}
          <div className="brutal-card bg-purple-50 border-purple-400">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-500 text-white flex items-center justify-center rounded">
                <Calendar size={24} />
              </div>
              <div>
                <p className="text-sm font-bold text-purple-700">UPCOMING SESSIONS</p>
                <p className="text-3xl font-bold">{sessions.stats.upcoming_count}</p>
              </div>
            </div>
          </div>

          {/* Completed This Month */}
          <div className="brutal-card bg-green-50 border-green-400">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-500 text-white flex items-center justify-center rounded">
                <CheckCircle size={24} />
              </div>
              <div>
                <p className="text-sm font-bold text-green-700">COMPLETED THIS MONTH</p>
                <p className="text-3xl font-bold">{sessions.stats.completed_this_month}</p>
              </div>
            </div>
          </div>

          {/* Allowance (External KJ) or Volunteer Status (RN Member) */}
          {sessions.stats.is_external_kj ? (
            <div className="brutal-card bg-amber-50 border-amber-400">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-500 text-white flex items-center justify-center rounded">
                  <DollarSign size={24} />
                </div>
                <div>
                  <p className="text-sm font-bold text-amber-700">ALLOWANCE THIS MONTH</p>
                  <p className="text-3xl font-bold">${sessions.stats.allowance_earned_this_month}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="brutal-card bg-blue-50 border-blue-400">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-500 text-white flex items-center justify-center rounded">
                  <User size={24} />
                </div>
                <div>
                  <p className="text-sm font-bold text-blue-700">MEMBER STATUS</p>
                  <p className="text-sm text-blue-600">Volunteer KJ</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Help Message - Updated for KJs */}
        <div className="brutal-card mb-8 bg-muted">
          <p className="font-bold mb-2">📞 Need Help?</p>
          <p className="text-sm">
            If you have questions about your assigned sessions or need support, 
            please contact us at <strong>{ORG_PHONE_RAW}</strong> or email <strong>{ORG_EMAIL_BOOKINGS}</strong>.
          </p>
          <button
            onClick={handleNeedHelp}
            className="brutal-btn-outline mt-3 text-sm flex items-center gap-2"
          >
            <Mail size={16} />
            SEND EMAIL
          </button>
        </div>

        <h2 className="text-xl sm:text-3xl font-syne font-bold mb-4 sm:mb-8">UPCOMING ASSIGNED SESSIONS</h2>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p>Loading your schedule...</p>
          </div>
        ) : sessions.upcoming_sessions.length === 0 ? (
          <div className="brutal-card text-center py-12" data-testid="no-sessions">
            <Calendar className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-bold">No upcoming assigned sessions</p>
            <p className="text-sm text-muted-foreground mt-2">
              Sessions will appear here when you are assigned as KJ
            </p>
            <div className="mt-6 p-4 bg-blue-50 border-2 border-blue-300 rounded max-w-md mx-auto">
              <p className="text-sm text-blue-800">
                <strong>💡 Want to book a karaoke session as a resident?</strong><br/>
                Use the <a href="/karaoke" className="underline font-bold">public booking calendar</a> - no login required!
              </p>
            </div>
          </div>
        ) : (
          <div className="grid gap-6">
            {sessions.upcoming_sessions.map((session) => (
              <div key={session.booking_code} className="brutal-card" data-testid="session-card">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4 flex-wrap">
                      <span className="font-mono text-2xl font-bold bg-amber-600 text-white px-4 py-2 border-2 border-black shadow-md notranslate" translate="no">
                        {session.booking_code}
                      </span>
                      {getStatusBadge(session.status)}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm font-bold text-muted-foreground">DATE</p>
                        <p className="font-mono text-lg">{formatDateWithDay(session.date)}</p>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-muted-foreground">TIME SLOT</p>
                        <p className="font-mono text-lg">{getSlotTime(session.slot_type)}</p>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-muted-foreground">RESIDENT</p>
                        <p className="font-mono text-lg notranslate" translate="no">{session.resident_name}</p>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-muted-foreground">SESSION RATE</p>
                        <p className="font-mono text-lg font-bold">${session.price}</p>
                      </div>
                    </div>

                    {session.block_unit && (
                      <div className="mt-3">
                        <p className="text-sm font-bold text-muted-foreground">LOCATION</p>
                        <p className="text-sm">{session.block_unit}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Past Sessions Preview */}
        {sessions.past_sessions.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-syne font-bold mb-4 flex items-center gap-2">
              <CheckCircle className="text-gray-500" />
              RECENT COMPLETED SESSIONS
            </h2>
            <div className="brutal-card bg-gray-50">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 text-left font-bold">Code</th>
                      <th className="p-3 text-left font-bold">Date</th>
                      <th className="p-3 text-left font-bold">Time</th>
                      <th className="p-3 text-left font-bold">Resident</th>
                      {sessions.stats.is_external_kj && (
                        <th className="p-3 text-left font-bold">Allowance</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.past_sessions.slice(0, 5).map((session) => (
                      <tr key={session.booking_code} className="border-b">
                        <td className="p-3 font-mono font-bold text-purple-600 notranslate" translate="no">
                          {session.booking_code}
                        </td>
                        <td className="p-3">{formatDate(session.date)}</td>
                        <td className="p-3">
                          {session.slot_type === 'afternoon' ? '1PM-5PM' : '6PM-10PM'}
                        </td>
                        <td className="p-3 notranslate" translate="no">{session.resident_name}</td>
                        {sessions.stats.is_external_kj && (
                          <td className="p-3">
                            {session.kj_allowance_earned ? (
                              <span className="font-bold text-green-600">$35</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {sessions.past_sessions.length > 5 && (
                <div className="p-3 text-center border-t">
                  <button
                    onClick={() => navigate('/kj-console')}
                    className="text-sm font-bold text-purple-600 hover:underline"
                  >
                    View All {sessions.past_sessions.length} Sessions →
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="brutal-card bg-blue-50 border-blue-400 mt-8">
          <h3 className="font-bold text-blue-800 mb-2">ℹ️ About Your Schedule</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• This page shows sessions where you are assigned as KJ</li>
            <li>• Sessions are assigned by residents (via preference) or by Admin</li>
            <li>• For any changes to your schedule, please contact the Admin team</li>
            <li>• Session status updates automatically after the booking day</li>
            {sessions.stats.is_external_kj && (
              <li>• As an External KJ, you earn $35 per completed session</li>
            )}
          </ul>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default DashboardPage;
